import { canCarry } from '../../../game/demo/inventory/capacity';
import { items, ItemId } from '../../../game/demo/inventory/items';
import { formatMoney } from '../../../game/demo/inventory/money';
import eventBus from '../../utils/emitter-event-bus';
import { InventoryEventBus, InventoryReader } from '../inventory/inventory.model';
import { NarratorEventBus } from '../narrator/narrator.model';
import { NavigatorEventBus, POI_ID } from '../navigator/navigator.model';
import { WorldEventBus } from '../world/world.model';
import { MerchantStock } from './merchant-stock';
import { MerchantEventBus } from './merchant.model';

type Tab = 'sell' | 'buy';

// Modal with Sell / Buy tabs. Sell lists the player's sellable items; selling
// pays coins and moves the item into the current merchant's per-POI stock, where
// it then appears under Buy. Buy spends coins to take an item back from stock.
// All inventory changes route through NarratorEventBus.nodeEntered (the only
// inventory mutation path); merchant stock is updated alongside.
export class MerchantUI {
  private readonly panel = document.querySelector<HTMLElement>('.js-merchant');
  private readonly list = document.querySelector<HTMLElement>('.js-merchant-items');
  private readonly close = document.querySelector<HTMLButtonElement>('.js-merchant-close');
  private readonly tabs = document.querySelector<HTMLElement>('.js-merchant-tabs');

  private currentPoi: POI_ID | null = null;
  private activeTab: Tab = 'sell';

  constructor(
    private readonly reader: InventoryReader,
    private readonly stock: MerchantStock,
  ) {
    eventBus.on(WorldEventBus.initialise, (poi) => (this.currentPoi = poi));
    eventBus.on(NavigatorEventBus.navigationStart, (poi) => {
      this.currentPoi = poi;
      this.hide();
    });
    eventBus.on(MerchantEventBus.open, () => this.open());
    eventBus.on(InventoryEventBus.changed, () => {
      if (this.panel && !this.panel.hidden) this.render();
    });

    this.close?.addEventListener('click', () => this.hide());
    this.tabs?.addEventListener('click', (event) => this.onTabClick(event));
    this.list?.addEventListener('click', (event) => this.onListClick(event));
  }

  private open(): void {
    if (!this.panel) return;
    this.activeTab = 'sell';
    this.panel.hidden = false;
    this.render();
  }

  private hide(): void {
    if (this.panel) this.panel.hidden = true;
  }

  private coins(): number {
    return this.reader.getState().items.coins ?? 0;
  }

  private owned(id: ItemId): number {
    return this.reader.getState().items[id] ?? 0;
  }

  private onTabClick(event: MouseEvent): void {
    const tab = (event.target as HTMLElement).dataset.tab as Tab | undefined;
    if (!tab || tab === this.activeTab) return;
    this.activeTab = tab;
    this.render();
  }

  private render(): void {
    if (!this.list) return;
    this.renderTabs();

    const rows = this.activeTab === 'sell' ? this.sellRows() : this.buyRows();
    this.list.innerHTML = '';

    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'merchant__empty';
      empty.textContent = this.activeTab === 'sell' ? 'Nothing to sell' : 'Nothing to buy';
      this.list.appendChild(empty);
      return;
    }

    const table = document.createElement('table');
    table.className = 'merchant__table';

    const head = document.createElement('tr');
    const cols: Array<[string, string, string?]> = [
      ['📦', 'merchant__col-item', 'Item'],
      ['#', 'merchant__col-qty', 'Quantity'],
      ['🪙', 'merchant__col-price', 'Price'],
      ['', 'merchant__col-action'],
    ];
    for (const [text, cls, title] of cols) {
      const th = document.createElement('th');
      th.className = cls;
      th.textContent = text;
      if (title) th.title = title;
      head.appendChild(th);
    }
    const thead = document.createElement('thead');
    thead.appendChild(head);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const row of rows) tbody.appendChild(row);
    table.appendChild(tbody);

    this.list.appendChild(table);
  }

  private renderTabs(): void {
    this.tabs?.querySelectorAll<HTMLElement>('[data-tab]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.tab === this.activeTab);
    });
  }

  private sellRows(): HTMLElement[] {
    return (Object.keys(items) as ItemId[])
      .filter((id) => items[id].sellable && (items[id].price ?? 0) > 0 && this.owned(id) > 0)
      .map((id) => this.row(id, this.owned(id), 'sell', true));
  }

  private buyRows(): HTMLElement[] {
    if (!this.currentPoi) return [];
    return this.stock.list(this.currentPoi).map(({ item, quantity }) => {
      const affordable = this.coins() >= (items[item].price ?? 0) && canCarry(this.reader.getState(), item);
      return this.row(item, quantity, 'buy', affordable);
    });
  }

  private row(id: ItemId, quantity: number, action: Tab, enabled: boolean): HTMLElement {
    const tr = document.createElement('tr');
    tr.className = 'merchant__item';

    const label = document.createElement('td');
    label.className = 'merchant__label';
    label.textContent = items[id].name;

    const qty = document.createElement('td');
    qty.className = 'merchant__qty';
    qty.textContent = String(quantity);

    const price = document.createElement('td');
    price.className = 'merchant__price';
    price.textContent = formatMoney(items[id].price ?? 0);

    const actionCell = document.createElement('td');
    actionCell.className = 'merchant__action-cell';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'merchant__action';
    button.textContent = action === 'sell' ? 'Sell' : 'Buy';
    button.setAttribute('data-action', action);
    button.setAttribute('data-item-id', id);
    button.disabled = !enabled;
    actionCell.appendChild(button);

    tr.append(label, qty, price, actionCell);
    return tr;
  }

  private onListClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const id = target.dataset.itemId as ItemId | undefined;
    const action = target.dataset.action as Tab | undefined;
    if (!id || !action || !this.currentPoi) return;

    const price = items[id].price ?? 0;
    if (price <= 0) return;

    if (action === 'sell') {
      if (this.owned(id) <= 0) return;
      this.stock.add(this.currentPoi, id);
      eventBus.emit(NarratorEventBus.nodeEntered, {
        effects: [
          { type: 'consume', item: id, quantity: 1 },
          { type: 'grant', item: 'coins', quantity: price },
        ],
      });
    } else {
      if (this.coins() < price || !canCarry(this.reader.getState(), id) || this.stock.count(this.currentPoi, id) <= 0) {
        return;
      }
      this.stock.remove(this.currentPoi, id);
      eventBus.emit(NarratorEventBus.nodeEntered, {
        effects: [
          { type: 'consume', item: 'coins', quantity: price },
          { type: 'grant', item: id, quantity: 1 },
        ],
      });
    }
  }
}
